import { useQuery } from '@tanstack/react-query';
import { httpClient } from '@/utils/httpClient';
import { configAdminService } from '@/services/configAdminService';
import { API_CONFIG } from '@/config/api';
import { queryKeys } from '@/lib/queryKeys';
import { useAuth } from '@/hooks/useAuth';

export const PEOPLE_SYNC_WORKER_CONFIG_KEY = 'system_health.people_sync_worker';

export type ComponentStatus = 'healthy' | 'degraded' | 'unavailable';

export interface ComponentHealth {
  name: string;
  status: ComponentStatus;
  description: string;
  responseTimeMs: number | null;
  details?: string;
}

export interface SystemHealthResponse {
  overallStatus: ComponentStatus;
  components: ComponentHealth[];
  checkedAt: string;
}

export interface SystemStatusResponse {
  status: ComponentStatus;
  demoSeedResetEnabled?: boolean;
}

/**
 * Fetches detailed system health for the admin System Health page.
 * Requires admin role.
 */
export const useSystemHealth = () => {
  const { isAuthenticated, isAdmin } = useAuth();
  return useQuery<SystemHealthResponse>({
    queryKey: queryKeys.systemHealth.detail,
    queryFn: () => httpClient.get<SystemHealthResponse>(API_CONFIG.ENDPOINTS.SYSTEM_HEALTH),
    enabled: isAuthenticated && isAdmin,
    refetchInterval: 30_000, // Poll every 30 seconds
    staleTime: 10_000,
  });
};

/**
 * Fetches lightweight system status for the degraded-health banner.
 * Available to all authenticated users (public endpoint, no admin required).
 */
export const useSystemStatus = () => {
  const { isAuthenticated } = useAuth();
  return useQuery<SystemStatusResponse>({
    queryKey: queryKeys.systemHealth.status,
    queryFn: () => httpClient.get<SystemStatusResponse>(API_CONFIG.ENDPOINTS.SYSTEM_STATUS),
    enabled: isAuthenticated,
    refetchInterval: 60_000, // Poll every 60 seconds
    staleTime: 30_000,
    retry: 1,
  });
};

/**
 * Reads the People Sync Worker enablement toggle from system_configuration.
 * Returns true (worker enabled) when the config row is missing.
 * Pass `{ enabled: false }` to skip the request where the value is not needed.
 */
export const usePeopleSyncWorkerEnabled = (options?: { enabled?: boolean }) => {
  const { isAuthenticated } = useAuth();
  return useQuery<boolean>({
    queryKey: queryKeys.adminConfig.list(PEOPLE_SYNC_WORKER_CONFIG_KEY),
    queryFn: async () => {
      const rows = await configAdminService.list(PEOPLE_SYNC_WORKER_CONFIG_KEY);
      const row = rows.find((r) => r.config_key === PEOPLE_SYNC_WORKER_CONFIG_KEY);
      const value = row?.config_value as { enabled?: boolean } | undefined;
      return value?.enabled ?? true;
    },
    enabled: isAuthenticated && (options?.enabled ?? true),
    staleTime: 30_000,
  });
};
